import type { ExternalCategory, Profile } from "@/hooks/useAuthQueries";

/**
 * External-identity UI capability predicates (TES-626, ADR 0008).
 *
 * External users (`profiles.external_user === true`) are Entra B2B guests / partners
 * bound to anchors (mig 308, TES-595). Their data lists are already anchor-filtered by
 * RLS — the UI only RENDERS read-only indication and hides write controls the backend
 * would reject anyway. The capability boundary mirrors mig 296 (TES-610):
 *
 *   - partner_supplier / external_auditor → fully read-only (no writes at all)
 *   - customer_acceptance                 → may write own defect_comments + append
 *                                            evidence to anchored defects; operative
 *                                            fields (status/assign/edit) stay locked.
 *
 * NOTE (R-SEC / ADR 0008): UI visibility is NOT the security boundary. RLS + the
 * `external_category_allows_evidence_write()` predicate enforce this server-side. These
 * helpers only govern which controls we render so an external user is never shown an
 * action the backend would reject.
 */

/** External categories that are fully read-only at the defect boundary. */
const EXTERNAL_READ_ONLY_CATEGORIES: readonly ExternalCategory[] = [
  "partner_supplier",
  "external_auditor",
] as const;

/** Minimal shape consumed by the predicates (the full Profile satisfies it). */
type ExternalProfileLike = Pick<Profile, "external_user" | "external_category"> | null | undefined;

/**
 * True when the user is an external identity that is fully read-only — i.e.
 * `external_user === true` AND `external_category ∈ {partner_supplier, external_auditor}`.
 *
 * Internal users (`external_user !== true`) are NEVER affected — the predicate is
 * strictly gated on the external flag so it can be OR-ed into existing read-only
 * checks without changing internal-user behaviour.
 */
export function isExternalReadOnly(profile: ExternalProfileLike): boolean {
  if (!profile || profile.external_user !== true) {
    return false;
  }
  return (
    profile.external_category != null &&
    EXTERNAL_READ_ONLY_CATEGORIES.includes(profile.external_category)
  );
}

/**
 * True when the user is an external `customer_acceptance` identity — a limited-write
 * external user that may comment and append evidence on its anchored defects but may
 * NOT change operative fields (status/assignment/edit). Internal users are never
 * affected (gated on `external_user === true`).
 */
export function isExternalLimitedWrite(profile: ExternalProfileLike): boolean {
  return profile?.external_user === true && profile.external_category === "customer_acceptance";
}

/**
 * True for ANY external identity at the defect boundary (read-only OR limited-write).
 * Used to decide whether to show the external "read-only view" indication and to lock
 * operative defect controls — both read-only and limited-write externals must not see
 * status/assign/edit affordances.
 */
export function isExternalDefectUser(profile: ExternalProfileLike): boolean {
  return isExternalReadOnly(profile) || isExternalLimitedWrite(profile);
}
